import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { SectionTitle } from "@/components/app/Bits";
import { Sparkles, Mail, X } from "lucide-react";
import { Logo } from "@/components/app/Logo";
import BrandHero from "@/components/app/BrandHero";

const DISMISS_KEY = "ydd_trial_banner_dismissed_at";
const TRIAL_BANNER_THRESHOLD = 7; // distinct days of completed prompts
const DISMISS_TTL_MS = 1000 * 60 * 60 * 24 * 14; // re-show after 2 weeks if still not Pro
const LESSON_BANNER_WINDOW_MS = 1000 * 60 * 60 * 24 * 7; // show for 7 days after upload
const LESSON_DISMISS_PREFIX = "ydd_lesson_banner_dismissed_";

export default function WelcomePage() {
  const { user } = useAuth();
  const [message, setMessage] = useState("");
  const [trialBanner, setTrialBanner] = useState({ show: false, days: 0 });
  const [latestLesson, setLatestLesson] = useState(null);

  useEffect(() => {
    api.get("/settings/welcome")
      .then(({ data }) => setMessage(data.message || ""))
      .catch(() => { /* harmless — falls back to default message */ });
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: sub }, { data: activity }] = await Promise.all([
          api.get("/subscription/me"),
          api.get("/prompts/activity-summary"),
        ]);
        if (sub?.active) return;
        if ((activity?.distinct_days_completed ?? 0) < TRIAL_BANNER_THRESHOLD) return;
        const dismissedAt = Number(localStorage.getItem(DISMISS_KEY) || 0);
        if (dismissedAt && Date.now() - dismissedAt < DISMISS_TTL_MS) return;
        setTrialBanner({ show: true, days: activity.distinct_days_completed });
      } catch (err) {
        console.warn("Welcome trial-banner fetch failed:", err);
      }
    })();
  }, []);

  const dismissBanner = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setTrialBanner((s) => ({ ...s, show: false }));
  };

  // Fetch most recent YDD lesson; show "What's new" banner for 7 days after upload.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/lessons");
        const newest = (data?.items || [])[0];
        if (!newest?.created_at) return;
        const uploadedAt = new Date(newest.created_at).getTime();
        if (Number.isNaN(uploadedAt)) return;
        if (Date.now() - uploadedAt > LESSON_BANNER_WINDOW_MS) return;
        if (localStorage.getItem(LESSON_DISMISS_PREFIX + newest.id)) return;
        setLatestLesson(newest);
      } catch (err) {
        console.warn("Welcome lessons fetch failed:", err);
      }
    })();
  }, []);

  const dismissLessonBanner = () => {
    if (latestLesson?.id) {
      localStorage.setItem(LESSON_DISMISS_PREFIX + latestLesson.id, String(Date.now()));
    }
    setLatestLesson(null);
  };

  return (
    <div className="space-y-8 max-w-3xl" data-testid="welcome-page">
      <BrandHero
        overline="Your Dog Days"
        title={`Hello, ${user?.name || "friend"}`}
        description={(<>Your at-a-glance home base for every dog you track &mdash; read the message from Karin, founder of Your Dog Days, below or jump right into <strong>Get Started</strong> in the sidebar.</>)}
        testid="welcome-brand-hero"
      />

      <SectionTitle overline="From Your Dog Days" title="Welcome note" />

      {latestLesson && (
        <Card
          className="border-2 border-primary shadow-none rounded-lg bg-card relative overflow-hidden"
          data-testid="whats-new-lesson-banner"
        >
          <button
            type="button"
            onClick={dismissLessonBanner}
            data-testid="dismiss-lesson-banner"
            className="absolute top-3 right-3 h-7 w-7 rounded-md flex items-center justify-center text-muted-foreground hover:bg-muted transition-colors"
            aria-label="Dismiss"
          >
            <X size={14} />
          </button>
          <CardContent className="p-5 sm:p-6 flex items-center gap-4 flex-wrap">
            <div className="h-11 w-11 rounded-md bg-primary text-primary-foreground flex items-center justify-center shrink-0">
              <Mail size={20} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">What&apos;s new</p>
              <p className="font-medium mt-1 truncate" data-testid="lesson-banner-title">
                New lesson: {latestLesson.title}
              </p>
            </div>
            <Link to="/lessons" data-testid="lesson-banner-cta" className="shrink-0">
              <Button size="sm" className="bg-primary text-primary-foreground hover:bg-primary/90">
                Read →
              </Button>
            </Link>
          </CardContent>
        </Card>
      )}

      {trialBanner.show && (
        <Card
          className="border-2 border-primary shadow-none rounded-lg bg-secondary relative overflow-hidden"
          data-testid="soft-trial-banner"
        >
          <button
            type="button"
            onClick={dismissBanner}
            data-testid="dismiss-trial-banner"
            className="absolute top-3 right-3 h-7 w-7 rounded-md flex items-center justify-center text-muted-foreground hover:bg-muted transition-colors"
            aria-label="Dismiss"
          >
            <X size={14} />
          </button>
          <CardContent className="p-5 sm:p-7 flex items-start gap-4 flex-wrap">
            <div className="h-11 w-11 rounded-md bg-primary text-primary-foreground flex items-center justify-center shrink-0">
              <Sparkles size={20} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{trialBanner.days} days &amp; counting</p>
              <h3 className="font-display text-xl sm:text-2xl font-extrabold tracking-tight mt-1">
                You&apos;ve kept this up for a week
              </h3>
              <p className="text-sm text-foreground/80 mt-2 max-w-prose">
                Nice work staying consistent. Unlock <strong>Pro</strong> and get an automatic email every morning
                so you never miss a session.
              </p>
              <div className="flex flex-wrap items-center gap-2 mt-4">
                <Link to="/subscription" data-testid="trial-banner-cta">
                  <Button className="bg-primary text-primary-foreground hover:bg-primary/90 active:scale-[0.98] transition-all">
                    <Mail size={14} className="mr-1.5" /> See Pro &amp; pricing
                  </Button>
                </Link>
                <Button variant="ghost" onClick={dismissBanner} data-testid="trial-banner-not-now">
                  Not right now
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="border-border shadow-none rounded-lg">
        <CardContent className="p-6 sm:p-10">
          <div className="flex items-center justify-center gap-4 mb-6 sm:mb-8" data-testid="welcome-note-header">
            <Logo variant="mark" size={64} />
            <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tighter text-center">
              Welcome to Your Dog Days
            </h2>
          </div>
          <article className="prose prose-stone max-w-none">
            {message.split("\n").map((para, i) => (
              // eslint-disable-next-line react/no-array-index-key -- paragraphs are content-only, no reordering
              <p key={`p-${i}-${para.slice(0, 24)}`} className="text-base leading-8 text-foreground/90 whitespace-pre-wrap">{para}</p>
            ))}
          </article>
        </CardContent>
      </Card>
    </div>
  );
}
